// components/CommunicationPreferences.tsx
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { CommunicationPreference, CommunicationReason, ValidationErrors } from '../types/customer';
import { elevation, colors } from '../theme';

interface CommunicationPreferencesProps {
  communicationPreference: CommunicationPreference;
  validationErrors: ValidationErrors;
  isDeceased: boolean;
  onInputChange: (e: any) => void;
  onReasonChange: (reason: CommunicationReason) => void;
}

export const CommunicationPreferences: React.FC<CommunicationPreferencesProps> = ({
  communicationPreference,
  validationErrors,
  isDeceased,
  onInputChange,
  onReasonChange,
}) => {
  const preferences = [
    { key: "allowEmail", label: "Email" },
    { key: "allowSms", label: "SMS" },
    { key: "allowPhone", label: "Phone" },
    { key: "allowLetter", label: "Letter" },
  ];

  return (
    <Paper
      sx={{
        mb: 3,
        mt: 3,
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 3,
        flexWrap: "wrap",
      }}
      elevation={elevation.paper}
    >
      <Box gridColumn="span 12">
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Marketing Communication Preferences
          {validationErrors.communicationPreference && (
            <Typography component="span" color="error" sx={{ ml: 1 }}>
              {validationErrors.communicationPreference}
            </Typography>
          )}
        </Typography>
        <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
          {preferences.map((pref) => {
            const value = (communicationPreference as any)[pref.key as keyof CommunicationPreference];
            return (
              <FormControl key={pref.key} sx={{ minWidth: 120 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  {pref.label}
                </Typography>
                <Box>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={
                      value === true
                        ? "true"
                        : value === false
                        ? "false"
                        : "null"
                    }
                    onChange={(_, newValue) => {
                      onInputChange({
                        target: {
                          name: `communicationPreference.${pref.key}`,
                          value: newValue,
                        },
                      });
                    }}
                    aria-label={pref.label}
                    disabled={isDeceased}
                  >
                    <ToggleButton value="true" aria-label="Allow">
                      <CheckIcon
                        sx={{
                          color:
                            value === true ? colors.mui.success : colors.mui.inactive,
                        }}
                      />
                    </ToggleButton>
                    <ToggleButton value="false" aria-label="Deny">
                      <CloseIcon
                        sx={{
                          color:
                            value === false ? colors.mui.error : colors.mui.inactive,
                        }}
                      />
                    </ToggleButton>
                    <ToggleButton value="null" aria-label="Unset">
                      <QuestionMarkIcon
                        sx={{
                          color:
                            value === null ? colors.mui.warning : colors.mui.inactive,
                        }}
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </FormControl>
            );
          })}
          <FormControl size="small" sx={{ minWidth: 180, maxWidth: 240 }}>
            <InputLabel id="comm-pref-reason-label">
              Reason for Change
            </InputLabel>
            <Select
              labelId="comm-pref-reason-label"
              name="communicationPreference.reason"
              value={
                communicationPreference.reason ??
                CommunicationReason.SystemDefault
              }
              label="Reason for Change"
              onChange={(e) => {
                const val = e.target.value;
                onReasonChange(
                  val === undefined
                    ? CommunicationReason.SystemDefault
                    : Number(val)
                );
              }}
              error={Boolean(
                validationErrors["communicationPreference.reason"]
              )}
              disabled={isDeceased}
            >
              <MenuItem value={CommunicationReason.SystemDefault}>
                System Default
              </MenuItem>
              <MenuItem value={CommunicationReason.NotCollected}>
                Not Collected
              </MenuItem>
              <MenuItem value={CommunicationReason.PhoneCall}>
                Phone Call
              </MenuItem>
              <MenuItem value={CommunicationReason.Email}>Email</MenuItem>
              <MenuItem value={CommunicationReason.Letter}>Letter</MenuItem>
              <MenuItem value={CommunicationReason.Other}>Other</MenuItem>
              <MenuItem value={CommunicationReason.OptomAudiologist}>
                Optom/Audiologist
              </MenuItem>
              <MenuItem value={CommunicationReason.Website}>
                Website
              </MenuItem>
              {isDeceased && (
                <MenuItem value={CommunicationReason.MarkedAsDeceased}>
                  Marked as deceased
                </MenuItem>
              )}
            </Select>
            {validationErrors["communicationPreference.reason"] && (
              <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                {validationErrors["communicationPreference.reason"]}
              </Typography>
            )}
          </FormControl>
        </Box>
      </Box>
    </Paper>
  );
};